import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import type { CareHistoryProvider } from '@lyrahealth-inc/shared-app-logic/src/models/providers/CareHistoryProviders'

import { useProviderPairCareHistory, useProviderPairConversations } from './useProviderPairConversations'
import { useProviderPairMessagingAccess } from './useProviderPairMessagingAccess'
import { getAuthUserId } from '../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import { useGetEpisodeParticipantsQuery } from '../../../episodes/data/episodesApi'
import { getProviderPairCareHistoryProfile, getProviderPairProgramNames } from '../providerPairCareHistory'
import {
  buildProviderPairDraftMessengerThread,
  buildProviderPairMessengerThread,
  buildProviderPairThread,
  getProviderPairProfile,
  getProviderPairThreadId,
} from '../providerPairUtils'

// `draftCounterpart` is the provider opened from Past care. Until a conversation with them is
// listed, they get a draft thread that creates the conversation on first send.
export const useProviderPairMessengerThreads = (draftCounterpart?: CareHistoryProvider) => {
  const providerId = useSelector(getAuthUserId)
  const clientId = useSelector(getSelectedClientId)
  const { canAccessProviderPairs } = useProviderPairMessagingAccess()
  const providerPairQuery = useProviderPairConversations()
  const visibleConversations = useMemo(
    () =>
      canAccessProviderPairs
        ? (providerPairQuery.currentData ?? []).filter(
            (conversation) =>
              conversation.patient_lyra_id === clientId && conversation.current_user.participant_lyra_id === providerId,
          )
        : [],
    [clientId, canAccessProviderPairs, providerId, providerPairQuery.currentData],
  )
  // A draft keeps care history loaded, so its first send never waits on profiles mid-promotion.
  const needsProfiles = visibleConversations.length > 0 || Boolean(draftCounterpart)
  const careHistoryQuery = useProviderPairCareHistory(!needsProfiles)
  // Fetch fallback names in parallel, but wait on the roster only when care history lacks a counterpart name.
  const rosterQuery = useGetEpisodeParticipantsQuery(
    { providerId },
    {
      skip: !canAccessProviderPairs || !providerId || !clientId || visibleConversations.length === 0,
    },
  )
  const careHistoryRequestSettled =
    !needsProfiles || careHistoryQuery.isError || careHistoryQuery.currentData !== undefined
  const rosterRequestSettled =
    visibleConversations.length === 0 || rosterQuery.isError || rosterQuery.currentData !== undefined
  const needsRosterNames = visibleConversations.some((conversation) =>
    conversation.participants.some(
      ({ participant_lyra_id: counterpartId }) =>
        counterpartId !== conversation.current_user.participant_lyra_id &&
        !careHistoryQuery.currentData?.some(
          (provider) => provider.participant_lyra_id === counterpartId && provider.display_name?.trim(),
        ),
    ),
  )
  const profilesReady = careHistoryRequestSettled && (!needsRosterNames || rosterRequestSettled)
  const resolveProfile = useCallback(
    (counterpartProviderId: string) =>
      getProviderPairCareHistoryProfile(
        careHistoryQuery.currentData ?? [],
        counterpartProviderId,
        clientId
          ? getProviderPairProfile(rosterQuery.currentData ?? [], clientId, counterpartProviderId)?.displayName
          : undefined,
      ),
    [careHistoryQuery.currentData, clientId, rosterQuery.currentData],
  )
  const listedThreads = useMemo(() => {
    if (!profilesReady) {
      return []
    }
    return visibleConversations.flatMap((conversation) => {
      const providerPairThread = buildProviderPairThread(conversation, resolveProfile)
      const messengerThread = providerPairThread ? buildProviderPairMessengerThread(providerPairThread) : null
      return messengerThread
        ? [
            {
              ...messengerThread,
              providerPairProgramNames: getProviderPairProgramNames(
                careHistoryQuery.currentData ?? [],
                messengerThread.recipientId,
                conversation.episode_ids,
              ),
            },
          ]
        : []
    })
  }, [careHistoryQuery.currentData, profilesReady, resolveProfile, visibleConversations])

  // Cached data can predate this mount, e.g. a list from before a deep-linked conversation existed.
  // The query refetches on mount, so wait for that fetch before treating a missing thread as gone.
  // Compare request ids, not timestamps: a frozen clock (cy.clock) makes the stale and new fetch times equal.
  const [cachedRequestId] = useState(() => (providerPairQuery.isFetching ? undefined : providerPairQuery.requestId))
  const mountFetchSettled =
    !providerPairQuery.isFetching && (cachedRequestId === undefined || providerPairQuery.requestId !== cachedRequestId)
  // Latch it, so later refetches (reconnect, after a send or read clear) don't unload the threads.
  const [hasFetchedSinceMount, setHasFetchedSinceMount] = useState(false)
  useEffect(() => {
    if (mountFetchSettled) {
      setHasFetchedSinceMount(true)
    }
  }, [mountFetchSettled])
  const fetchedSinceMount = hasFetchedSinceMount || mountFetchSettled
  const pairRequestSettled =
    !canAccessProviderPairs ||
    providerPairQuery.isUninitialized ||
    providerPairQuery.isError ||
    (providerPairQuery.currentData !== undefined && fetchedSinceMount)
  const threadsLoaded = pairRequestSettled && profilesReady

  // Shared by the draft and the listed thread that replaces it, so callers can select by it.
  const draftThreadId =
    canAccessProviderPairs &&
    draftCounterpart &&
    clientId &&
    providerId &&
    draftCounterpart.participant_lyra_id !== providerId
      ? getProviderPairThreadId(clientId, providerId, draftCounterpart.participant_lyra_id)
      : undefined
  // A listed unreadable pair must not be offered as a fresh draft.
  const draftPairIsListed = Boolean(
    draftCounterpart &&
    visibleConversations.some(({ participants }) =>
      participants.some(({ participant_lyra_id: id }) => id === draftCounterpart.participant_lyra_id),
    ),
  )
  const pairListUnavailable = providerPairQuery.isError && providerPairQuery.currentData === undefined
  const draftPairIsShown = listedThreads.some((thread) => thread.id === draftThreadId)
  // Wait for the list so an existing conversation never flashes as an empty draft first.
  const threads = useMemo(() => {
    if (
      !draftThreadId ||
      !draftCounterpart ||
      !clientId ||
      !threadsLoaded ||
      draftPairIsListed ||
      pairListUnavailable
    ) {
      return listedThreads
    }
    return [
      ...listedThreads,
      buildProviderPairDraftMessengerThread(
        clientId,
        providerId,
        getProviderPairCareHistoryProfile([draftCounterpart], draftCounterpart.participant_lyra_id),
      ),
    ]
  }, [
    clientId,
    draftCounterpart,
    draftPairIsListed,
    draftThreadId,
    listedThreads,
    pairListUnavailable,
    providerId,
    threadsLoaded,
  ])

  return {
    threads,
    draftThreadId,
    draftUnavailable: Boolean(
      draftThreadId && threadsLoaded && (pairListUnavailable || (draftPairIsListed && !draftPairIsShown)),
    ),
    isLoading: providerPairQuery.isLoading || (!profilesReady && (careHistoryQuery.isLoading || rosterQuery.isLoading)),
    threadsLoaded,
  }
}
